import { useCallback, useEffect, useState } from 'react';
import { bbox } from '@turf/turf';
import { asQuantityWithUnit } from '../../utils/formatters';
import { usePortfolioEvaluatorContext } from '../../contexts/PortfolioEvaluatorContext';
import IconButton from '../basics/IconButton';
import XIcon from '../icons/XIcon';
import MapPopup from '../map/MapPopup';
import { ZOOM_THRESHOLD } from './constants';
import { layerIdForTileset } from '../../utils/tilesets';

/**
 * How do we decide where the popup should be?
 * - Calculate the bounding box
 * - Calculate the center of the bounding box
 * - Set the offsets from the center of the bounding box so that it will be outside the bounding box
 */
const calculateOffsetsAndLatLng = (map, featureData) => {
  const zoom = map.getZoom();
  const FIXED_OFFSET = zoom > ZOOM_THRESHOLD ? 10 : 15;

  const boundingBox = bbox(featureData);

  // get height and width of boundingBox
  const [minX, minY, maxX, maxY] = boundingBox;
  // get the center of the bounding box
  const center = [(minX + maxX) / 2, (minY + maxY) / 2];

  // Get height & width in pixels
  const { x: minXPixels, y: minYPixels } = map.project([minX, minY]);
  const { x: maxXPixels, y: maxYPixels } = map.project([maxX, maxY]);

  const width = maxXPixels - minXPixels;
  const height = minYPixels - maxYPixels;

  const widthOffset = width / 2 + FIXED_OFFSET;
  const heightOffset = height / 2 + FIXED_OFFSET;

  return {
    offsets: {
      // if the top of the popup is the anchor, then the popup should be below the center
      top: [0, heightOffset],
      'top-left': [widthOffset, heightOffset],
      'top-right': [-widthOffset, heightOffset],
      bottom: [0, -heightOffset],
      'bottom-left': [widthOffset, -heightOffset],
      'bottom-right': [-widthOffset, -heightOffset],
      left: [widthOffset, 0],
      right: [-widthOffset, 0],
    },
    lngLat: center,
  };
};

/**
 * Recalculate the offsets and lngLat when the map zooms or the featureData changes.
 */
const useOffsetsAndLngLat = (map, featureData) => {
  const [state, setState] = useState(
    calculateOffsetsAndLatLng(map, featureData),
  );

  const update = useCallback(() => {
    if (!map) return;
    setState(calculateOffsetsAndLatLng(map, featureData));
  }, [map, featureData]);

  useEffect(() => {
    update();
    map.on('zoom', update);
    return () => {
      map.off('zoom', update);
    };
  }, [map, update]);

  return state;
};

const InfoCard = ({ mapRef, featureData, onClose }) => {
  const { tilesets } = usePortfolioEvaluatorContext();
  const { area, suitability } = featureData?.properties || {};
  const { offsets, lngLat } = useOffsetsAndLngLat(mapRef.current, featureData);

  return (
    <MapPopup mapRef={mapRef} lngLat={lngLat} offset={offsets} className="p-1">
      <div>
        {/* HEADER */}
        <div className="d-flex">
          {/* TITLE */}
          <div className="d-flex pl-3 pt-3 pb-1 pr-4 min-w-0">
            <div className="d-block">
              <h2 className="m-0 text-truncate">Plot Overview</h2>
              <div className="flex-shrink-0">
                {asQuantityWithUnit('acres', 'acre')(area)},{' '}
                {featureData?.properties?.usState.abbreviation}
              </div>
            </div>
          </div>

          {/* BUTTONS */}
          <div className="d-flex ml-auto pr-1 align-items-start">
            <IconButton
              color="link"
              size="sm"
              onClick={onClose}
              className="px-1 py-2 border-0"
            >
              <XIcon width={10} height={10} className="d-block" />
            </IconButton>
          </div>
        </div>

        {/* BODY */}
        <div className="px-3 pb-3">
          {/* CROPS */}
          {tilesets.map(({ id, displayName }) => {
            const layerId = layerIdForTileset(id);
            const suitableAcres = suitability?.[layerId];
            return suitableAcres ? (
              <div key={id}>
                {displayName}: {suitableAcres.toFixed(0)} acres
              </div>
            ) : null;
          })}
        </div>
      </div>
    </MapPopup>
  );
};

export default InfoCard;
